import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'

import { practitionerRoutes } from '../utils/integrationConfig'

import {
  PractitionerApiError,
  createPractitionerAccount,
  describeError,
  signInPractitioner,
  startPractitionerSubscription,
} from '../utils/practitionerApi'

import type {
  MembershipPlanId,
  PractitionerAccountInput,
  PractitionerSession,
  SignupStep,
} from '../types/practitionerSignup'

function stepFromPath(
  path: string,
): SignupStep {
  return path.startsWith(
    practitionerRoutes.membership,
  )
    ? 'membership'
    : 'account'
}

function isSessionValid(
  session: PractitionerSession | null,
): session is PractitionerSession {
  return Boolean(
    session &&
      session.expiresAt - 60_000 >
        Date.now(),
  )
}

export function usePractitionerSignup(
  initialStep: SignupStep,
) {
  const [step, setStep] =
    useState<SignupStep>(initialStep)

  const [session, setSession] =
    useState<PractitionerSession | null>(null)

  const [selectedPlan, setSelectedPlan] =
    useState<MembershipPlanId | null>(
      null,
    )

  const [paymentModalPlan, setPaymentModalPlan] =
    useState<MembershipPlanId | null>(
      null,
    )

  const [accountSubmitting, setAccountSubmitting] =
    useState(false)

  const [accountError, setAccountError] =
    useState<PractitionerApiError | null>(
      null,
    )

  const [needsSignIn, setNeedsSignIn] =
    useState(false)

  const [signInSubmitting, setSignInSubmitting] =
    useState(false)

  const [signInError, setSignInError] =
    useState<string | null>(null)

  const [redirectingPlan, setRedirectingPlan] =
    useState<MembershipPlanId | null>(
      null,
    )

  const [checkoutError, setCheckoutError] =
    useState<string | null>(null)

  const checkoutCancelled = useMemo(
    () =>
      new URLSearchParams(
        window.location.search,
      ).get('checkout') === 'cancelled',
    [],
  )

  useEffect(() => {
    const onPopState = () => {
      setStep(
        stepFromPath(
          window.location.pathname,
        ),
      )
    }

    window.addEventListener(
      'popstate',
      onPopState,
    )

    return () =>
      window.removeEventListener(
        'popstate',
        onPopState,
      )
  }, [])

  const goTo = useCallback(
    (next: SignupStep) => {
      const path =
        next === 'membership'
          ? practitionerRoutes.membership
          : practitionerRoutes.join

      if (
        window.location.pathname !== path
      ) {
        window.history.pushState(
          null,
          '',
          path,
        )
      }

      setStep(next)

      window.scrollTo({
        top: 0,
      })
    },
    [],
  )

  const submitAccount = useCallback(
    async (
      input: PractitionerAccountInput,
    ) => {
      setAccountSubmitting(true)
      setAccountError(null)

      try {
        await createPractitionerAccount(
          input,
        )
      } catch (error) {
        if (
          error &&
          typeof error === 'object' &&
          'code' in error &&
          'message' in error
        ) {
          setAccountError(
            new PractitionerApiError(
              String(error.code),
              String(error.message),
            ),
          )
        } else {
          setAccountError(
            new PractitionerApiError(
              'server',
              describeError(error),
            ),
          )
        }

        setAccountSubmitting(false)
        return
      }

      try {
        const nextSession =
          await signInPractitioner(
            input.email,
            input.password,
          )

        setSession(nextSession)
      } catch (error) {
        setNeedsSignIn(true)
        setSignInError(
          describeError(error),
        )
      }

      setAccountSubmitting(false)

      goTo('membership')
    },
    [goTo],
  )

  /**
   * Clicking a membership plan opens the
   * Fem Entity payment confirmation modal.
   */
  const choosePlan = useCallback(
    (planId: MembershipPlanId) => {
      console.log(
        '[Practitioner Signup] Plan selected:',
        planId,
      )

      setSelectedPlan(planId)
      setCheckoutError(null)

      if (!isSessionValid(session)) {
        console.log(
          '[Practitioner Signup] No valid session. Sign-in required.',
        )

        setNeedsSignIn(true)
        return
      }

      console.log(
        '[Practitioner Signup] Opening payment modal:',
        planId,
      )

      setPaymentModalPlan(planId)
    },
    [session],
  )

  /**
   * Close the Fem Entity confirmation modal.
   */
  const cancelPaymentModal =
    useCallback(() => {
      console.log(
        '[Practitioner Signup] Payment modal cancelled.',
      )

      setPaymentModalPlan(null)
      setCheckoutError(null)
    }, [])

  /**
   * Called when the practitioner presses
   * "Continue to secure payment".
   *
   * This calls the Supabase Edge Function.
   *
   * The Edge Function creates a Stripe Checkout
   * Session and returns checkoutUrl.
   *
   * The browser is then redirected directly
   * to Stripe Checkout.
   */
  const continueToPayment =
    useCallback(async () => {
      console.log(
        '[Practitioner Signup] Continue to secure payment clicked.',
      )

      if (!paymentModalPlan) {
        console.error(
          '[Practitioner Signup] No payment modal plan selected.',
        )
        return
      }

      if (!isSessionValid(session)) {
        console.error(
          '[Practitioner Signup] Session is missing or expired.',
        )

        setPaymentModalPlan(null)
        setNeedsSignIn(true)
        return
      }

      const planId =
        paymentModalPlan

      console.log(
        '[Practitioner Signup] Starting Stripe Checkout:',
        {
          planId,
          userId: session.userId,
        },
      )

      setCheckoutError(null)
      setRedirectingPlan(planId)

      try {
        const result =
          await startPractitionerSubscription(
            planId,
            session,
          )

        console.log(
          '[Practitioner Signup] Stripe Checkout response:',
          {
            success: result.success,
            checkoutSessionId:
              result.checkoutSessionId,
            customerId:
              result.customerId,
            tierName:
              result.tierName,
            planType:
              result.planType,
            trialEndDate:
              result.trialEndDate,
            hasCheckoutUrl:
              Boolean(result.checkoutUrl),
          },
        )

        if (!result.checkoutUrl) {
          throw new PractitionerApiError(
            'server',
            'Stripe did not return a checkout URL. Please try again.',
          )
        }

        console.log(
          '[Practitioner Signup] Redirecting to Stripe Checkout.',
        )

        setPaymentModalPlan(null)

        /**
         * Stripe Checkout cannot run inside an iframe.
         *
         * Magic Patterns preview can render the website
         * inside an iframe, so navigate the top-level
         * browser window instead.
         */
        if (window.top) {
          window.top.location.href =
            result.checkoutUrl
        } else {
          window.location.href =
            result.checkoutUrl
        }
      } catch (error) {
        console.error(
          '[Practitioner Signup] Stripe Checkout failed:',
          error,
        )

        setCheckoutError(
          describeError(error),
        )
      } finally {
        setRedirectingPlan(null)
      }
    }, [
      paymentModalPlan,
      session,
    ])

  /**
   * Sign in an existing practitioner and then
   * reopen the selected plan's payment modal.
   */
  const resumeWithSignIn =
    useCallback(
      async (
        email: string,
        password: string,
      ) => {
        setSignInSubmitting(true)
        setSignInError(null)

        try {
          const nextSession =
            await signInPractitioner(
              email,
              password,
            )

          setSession(nextSession)
          setNeedsSignIn(false)

          if (selectedPlan) {
            console.log(
              '[Practitioner Signup] Sign-in successful. Opening payment modal:',
              selectedPlan,
            )

            setPaymentModalPlan(
              selectedPlan,
            )
          }
        } catch (error) {
          setSignInError(
            describeError(error),
          )
        } finally {
          setSignInSubmitting(false)
        }
      },
      [selectedPlan],
    )

  return {
    step,
    goTo,

    session,

    selectedPlan,

    accountSubmitting,
    accountError,
    submitAccount,

    choosePlan,

    paymentModalPlan,
    cancelPaymentModal,
    continueToPayment,

    redirectingPlan,

    checkoutError,
    checkoutCancelled,

    needsSignIn,
    signInSubmitting,
    signInError,
    resumeWithSignIn,

    cancelSignIn: () =>
      setNeedsSignIn(false),
  }
}
